/**
 * React Query hook for Risk Breakdown Dashboard
 *
 * Fetches CEO-friendly risk component breakdown including:
 * - Geographic Risk (25%)
 * - Product Risk (15%)
 * - Channel Risk (20%)
 * - Customer Risk (40%)
 *
 * Plus jurisdiction comparison and top risk drivers.
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth';
import { ApplicantsService } from '../services';
import { useMemo } from 'react';

// Query key factory
export const riskBreakdownKeys = {
  all: ['risk', 'breakdown'],
  detail: (applicantId) => [...riskBreakdownKeys.all, applicantId],
};

/**
 * Hook to get a memoized ApplicantsService instance
 */
function useApplicantsService() {
  const { getToken } = useAuth();
  return useMemo(() => new ApplicantsService(getToken), [getToken]);
}

/**
 * Fetch risk breakdown for an applicant
 *
 * @param {string} applicantId - Applicant ID
 * @param {Object} options - Additional React Query options
 * @returns Query result with risk breakdown data
 */
export function useRiskBreakdown(applicantId, options = {}) {
  const service = useApplicantsService();

  return useQuery({
    queryKey: riskBreakdownKeys.detail(applicantId),
    queryFn: ({ signal }) => service.getRiskBreakdown(applicantId, { signal }),
    enabled: !!applicantId,
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Poll every 60s for updates
    ...options,
  });
}

export default useRiskBreakdown;
